import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import CreatePost from '../components/CreatePost';
import PostCard from '../components/PostCard';
import { PostListSkeleton } from '../components/Skeletons';
import StoriesReel from '../components/StoriesReel';
import { CameraModal } from '../components/LazyModals';
import { publishStory } from '../utils/stories';
import { useToast } from '../contexts/ToastContext';
import StoryViewerModal from '../components/StoryViewerModal';
import QuoteBazzModal from '../components/QuoteBazzModal';
import PullToRefresh from '../components/PullToRefresh';
import { readCache, writeCache } from '../utils/localCache';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { getMediaUrl } from '../utils/mediaUrl';

const Home = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [feedType, setFeedType] = useState('following');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [unreadFollowing, setUnreadFollowing] = useState(0);
  const [unreadHybrid, setUnreadHybrid] = useState(0);
  const [communitiesWithUnread, setCommunitiesWithUnread] = useState([]);
  const [neighborhoodCoords, setNeighborhoodCoords] = useState(null);
  const [neighborhoodLocationError, setNeighborhoodLocationError] = useState(null);
  const [neighborhoodLocationLoading, setNeighborhoodLocationLoading] = useState(false);
  const loadMoreRef = useRef(null);
  const socket = useSocket();

  // Stories State
  const [isStoryCameraOpen, setIsStoryCameraOpen] = useState(false);
  const [sharingStory, setSharingStory] = useState(false);
  const toast = useToast();
  const [selectedStoryGroup, setSelectedStoryGroup] = useState(null);
  const [selectedStoryIndex, setSelectedStoryIndex] = useState(0);
  const [allStoryGroups, setAllStoryGroups] = useState([]);
  const [storiesRefreshKey, setStoriesRefreshKey] = useState(0);
  const [quoteBazzPost, setQuoteBazzPost] = useState(null);

  // True while the feed is showing cached posts that a fetch will replace
  const showingCachedRef = useRef(false);

  // Publish what was captured in the story camera; the camera closes right away
  // and the upload continues in the background
  const handleStoryCapture = async (files, caption) => {
    const file = files?.[0];
    if (!file || sharingStory) return;
    setSharingStory(true);
    toast.info(t('stories.sharing', 'Sharing your story…'));
    try {
      await publishStory(file, caption);
      toast.success(t('stories.createdSuccess', 'Story shared successfully!'));
      setStoriesRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error('Create story error:', err);
      toast.error(
        err.code === 'too_large'
          ? t('stories.fileTooLarge', 'Media file exceeds 25MB limit')
          : err.response?.data?.error || t('stories.createFailed', 'Failed to share story')
      );
    } finally {
      setSharingStory(false);
    }
  };

  const fetchPosts = async (cursor = null, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else if (!showingCachedRef.current) {
        setLoading(true);
      }
      
      let url;
      if (feedType === 'neighborhood' && neighborhoodCoords) {
        url = cursor
          ? `/api/posts/feed?type=neighborhood&lat=${neighborhoodCoords.lat}&lon=${neighborhoodCoords.lon}&radius_km=10&cursor=${cursor}&limit=20`
          : `/api/posts/feed?type=neighborhood&lat=${neighborhoodCoords.lat}&lon=${neighborhoodCoords.lon}&radius_km=10&limit=20`;
      } else if (feedType !== 'neighborhood') {
        url = cursor
          ? `/api/posts/feed?type=${feedType}&cursor=${cursor}&limit=20`
          : `/api/posts/feed?type=${feedType}&limit=20`;
      } else {
        setLoading(false);
        setLoadingMore(false);
        return;
      }
      
      const response = await axios.get(url);
      
      if (append) {
        setPosts(prev => [...prev, ...response.data.posts]);
      } else {
        setPosts(response.data.posts);
        showingCachedRef.current = false;
        // Neighborhood results depend on location, so they aren't cached
        if (feedType !== 'neighborhood') {
          writeCache(`feed:${feedType}`, (response.data.posts || []).slice(0, 20));
        }
      }
      
      setHasMore(response.data.hasMore);
      setNextCursor(response.data.nextCursor);
      if (response.data.unreadCount !== undefined && !append) {
        if (feedType === 'following') setUnreadFollowing(0);
        else setUnreadHybrid(0);
      }
    } catch (error) {
      console.error('Fetch posts error:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (nextCursor && !loadingMore) {
      fetchPosts(nextCursor, true);
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      const { data } = await axios.get('/api/posts/feed-unread-counts');
      setUnreadFollowing(data.following ?? 0);
      setUnreadHybrid(data.hybrid ?? 0);
    } catch (err) {
      console.error('Fetch unread counts error:', err);
    }
  };

  const fetchCommunitiesWithUnread = async () => {
    try {
      const { data } = await axios.get('/api/communities/with-unread');
      setCommunitiesWithUnread(data.communities ?? []);
    } catch (err) {
      console.error('Fetch communities with unread error:', err);
    }
  };

  // When switching to Neighborhood, request location if we don't have it yet
  const requestNeighborhoodLocation = () => {
    if (!navigator.geolocation) {
      setNeighborhoodLocationError(t('home.locationNotSupported'));
      return;
    }
    setNeighborhoodLocationError(null);
    setNeighborhoodLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setNeighborhoodCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setNeighborhoodLocationLoading(false);
      },
      () => {
        setNeighborhoodLocationError(t('home.locationPermissionNeighborhood'));
        setNeighborhoodLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    setPosts([]);
    setNextCursor(null);
    showingCachedRef.current = false;
    if (feedType !== 'neighborhood') {
      const cachedPosts = readCache(`feed:${feedType}`);
      if (cachedPosts?.length) {
        setPosts(cachedPosts);
        setHasMore(false); // no valid cursor until the fresh page arrives
        setLoading(false);
        showingCachedRef.current = true;
      }
    }
    if (feedType === 'neighborhood') {
      if (neighborhoodCoords) fetchPosts();
      else {
        setLoading(false);
        if (!neighborhoodLocationLoading && !neighborhoodLocationError) requestNeighborhoodLocation();
      }
    } else {
      fetchPosts();
    }
  }, [feedType]);

  useEffect(() => {
    if (feedType === 'neighborhood' && neighborhoodCoords) {
      setPosts([]);
      setNextCursor(null);
      fetchPosts();
    }
  }, [neighborhoodCoords?.lat, neighborhoodCoords?.lon]);

  useEffect(() => {
    if (location.pathname === '/home') {
      fetchUnreadCounts();
      fetchCommunitiesWithUnread();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (socket) {
      socket.on('new-post', () => {
        fetchPosts();
      });

      socket.on('post-deleted', () => {
        fetchPosts();
      });

      socket.on('new-comment', () => {
        fetchPosts();
      });

      return () => {
        socket.off('new-post');
        socket.off('post-deleted');
        socket.off('new-comment');
      };
    }
  }, [socket]);

  // Refetch feed when returning from compose
  useEffect(() => {
    if (location.pathname === '/home' && location.state?.refreshFeed) {
      fetchPosts();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state?.refreshFeed]);

  // Infinite scroll: load more when sentinel enters viewport
  useEffect(() => {
    if (!hasMore || loadingMore || loading) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && nextCursor && !loadingMore) loadMore();
      },
      { rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, nextCursor]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
        {currentUser && (
          <StoriesReel
            refreshTrigger={storiesRefreshKey}
            onOpenCreateStory={() => setIsStoryCameraOpen(true)}
            onSelectUserStory={(group, index, allGroups) => {
              setSelectedStoryGroup(group);
              setSelectedStoryIndex(index);
              setAllStoryGroups(allGroups || [group]);
            }}
          />
        )}

        <div className="sticky top-0 z-10 bg-white dark:bg-gray-950 border-b border-gray-100 dark:border-gray-800">
          <div className="flex">
            <button
              onClick={() => setFeedType('following')}
              className={`flex-1 py-3 text-center font-medium text-sm transition-colors relative ${
                feedType === 'following'
                  ? 'text-gray-900 dark:text-gray-100 border-b-2 border-gray-900 dark:border-gray-100'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {t('home.following')}
              {unreadFollowing > 0 && (
                <span className="absolute -top-0.5 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full">
                  {unreadFollowing > 99 ? '99' : unreadFollowing}
                </span>
              )}
            </button>
            <button
              onClick={() => setFeedType('hybrid')}
              className={`flex-1 py-3 text-center font-medium text-sm transition-colors relative ${
                feedType === 'hybrid'
                  ? 'text-gray-900 dark:text-gray-100 border-b-2 border-gray-900 dark:border-gray-100'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {t('home.forYou')}
              {unreadHybrid > 0 && (
                <span className="absolute -top-0.5 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full">
                  {unreadHybrid > 99 ? '99' : unreadHybrid}
                </span>
              )}
            </button>
            <button
              onClick={() => setFeedType('neighborhood')}
              className={`flex-1 py-3 text-center font-medium text-sm transition-colors ${
                feedType === 'neighborhood'
                  ? 'text-gray-900 dark:text-gray-100 border-b-2 border-gray-900 dark:border-gray-100'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {t('home.neighborhood')}
            </button>
          </div>
        </div>

        {communitiesWithUnread.length > 0 && (
          <div className="px-4 py-2 bg-primary-500/10 dark:bg-primary-500/20 border-b border-gray-100 dark:border-gray-800">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              {t('home.newBuzzesInCommunities')}
            </p>
            <div className="flex flex-wrap gap-2">
              {communitiesWithUnread.map((c) => (
                <Link
                  key={c.id}
                  to={`/communities/${c.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <i className="fas fa-users text-primary-500 text-xs" />
                  <span>{c.name}</span>
                  <span className="min-w-[20px] h-5 px-1 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full">
                    {c.unreadBuzzCount > 99 ? '99' : c.unreadBuzzCount}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="hidden md:block">
          <CreatePost onPostCreated={fetchPosts} />
        </div>

        {feedType === 'neighborhood' && !neighborhoodCoords && !neighborhoodLocationError && neighborhoodLocationLoading ? (
          <div className="text-center py-8 text-gray-400 dark:text-gray-500 font-light text-sm">{t('home.gettingLocation')}</div>
        ) : feedType === 'neighborhood' && neighborhoodLocationError ? (
          <div className="text-center py-8 px-4">
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">{neighborhoodLocationError}</p>
            <button
              type="button"
              onClick={requestNeighborhoodLocation}
              className="px-4 py-2 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-sm font-medium"
            >
              {t('home.tryAgainLocation')}
            </button>
          </div>
        ) : loading ? (
          <PostListSkeleton />
        ) : posts.length === 0 ? (
          <div className="text-center py-8 text-gray-400 dark:text-gray-500 font-light text-sm">
            {feedType === 'neighborhood' ? t('home.noNeighborhoodBuzzes') : t('home.noPosts')}
          </div>
        ) : (
          <PullToRefresh onRefresh={fetchPosts}>
            <div>
              {posts.map((post) => (
                <div key={post.reposter ? `repost-${post.reposter.id}-${post.repostedAt}-${post.id}` : post.id} className="border-b border-gray-100 dark:border-gray-800">
                  {post.reposter && (
                    <div className="px-4 pt-2 pb-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <i className="fas fa-retweet text-gray-400 dark:text-gray-500" />
                      {post.reposter.id === currentUser?.id ? (
                        <span>{t('home.youReposted')}</span>
                      ) : (
                        <Link to={`/profile/${post.reposter.handle}`} className="flex items-center gap-2 hover:text-gray-700 dark:hover:text-gray-300">
                          {post.reposter.profilePicture ? (
                            <img src={getMediaUrl(post.reposter.profilePicture)} alt="" className="w-5 h-5 rounded-full object-cover" />
                          ) : (
                            <span className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                              <i className="fas fa-user text-gray-400 dark:text-gray-500 text-xs" />
                            </span>
                          )}
                          <span className="font-medium text-gray-700 dark:text-gray-300">{post.reposter.username}</span>
                          <span>{t('home.reposted')}</span>
                        </Link>
                      )}
                    </div>
                  )}
                  <PostCard post={post} onUpdate={fetchPosts} onQuoteBazz={(p) => setQuoteBazzPost(p)} />
                </div>
              ))}
              {hasMore && (
                <div ref={loadMoreRef} className="py-4 flex justify-center min-h-[24px]">
                  {loadingMore && (
                    <span className="text-sm text-gray-400 dark:text-gray-500">{t('common.loading')}</span>
                  )}
                </div>
              )}
            </div>
          </PullToRefresh>
        )}

      {/* Quote Bazz Modal */}
      {quoteBazzPost && (
        <QuoteBazzModal
          post={quoteBazzPost}
          onClose={() => setQuoteBazzPost(null)}
          onSuccess={() => {
            setQuoteBazzPost(null);
            fetchPosts();
          }}
        />
      )}

      {/* Story Modals */}
      {/* Adding a story opens the in-app camera (capture or pick from gallery) */}
      <CameraModal
        isOpen={isStoryCameraOpen}
        onClose={() => setIsStoryCameraOpen(false)}
        onCapture={handleStoryCapture}
        maxPhotos={1}
        initialAspectRatio="9:16"
        submitLabel={t('stories.shareToStory', 'Share to Story')}
      />

      {selectedStoryGroup && (
        <StoryViewerModal
          isOpen={!!selectedStoryGroup}
          initialUserGroup={selectedStoryGroup}
          initialStoryIndex={selectedStoryIndex}
          allUserGroups={allStoryGroups}
          onClose={() => setSelectedStoryGroup(null)}
          onStoryDeleted={() => setStoriesRefreshKey((prev) => prev + 1)}
        />
      )}
    </div>
  );
};

export default Home;
